/**
 * Felt geometry — the betting-layout side of the domain.
 *
 * The felt grid holds 1–36 in twelve streets of three. Row 0 is the top row
 * (3, 6, 9 … 36), row 1 the middle (2, 5, 8 … 35), row 2 the bottom
 * (1, 4, 7 … 34). Zero pockets sit in a head column at `col === -1`.
 *
 * Everything here is pure geometry: which cells are adjacent, and therefore
 * which splits, streets, corners and six lines are legal. Payouts and coverage
 * live in `bets.ts`.
 */

import type { PocketId, WheelStyle } from './types';

export const FELT_COLS = 12;
export const FELT_ROWS = 3;

/** Grid cell of a numbered pocket. */
export interface FeltCell {
  readonly col: number;
  readonly row: number;
}

/** A zero cell in the head column, expressed as a fractional row span. */
export interface ZeroCell {
  readonly id: PocketId;
  readonly rowStart: number;
  readonly rowEnd: number;
}

/** Number occupying a grid cell. `col` 0–11, `row` 0–2. */
export function feltNumber(col: number, row: number): number {
  return col * 3 + (FELT_ROWS - row);
}

/** Grid cell for a number 1–36. */
export function feltCellOf(value: number): FeltCell {
  if (!Number.isInteger(value) || value < 1 || value > 36) {
    throw new Error(`No felt cell for ${value}`);
  }
  const col = Math.floor((value - 1) / 3);
  const row = FELT_ROWS - (value - col * 3);
  return { col, row };
}

const NUMBER_IDS: readonly PocketId[] = Array.from(
  { length: 36 },
  (_, i) => String(i + 1) as PocketId,
);

/**
 * Zero cells for a style.
 *
 * On the double-zero felt `00` sits against the top half (touching 3 and 2) and
 * `0` against the bottom half (touching 2 and 1) — which is what makes the
 * trios `00-2-3` and `0-1-2` the legal ones.
 */
export function zeroCells(style: WheelStyle): readonly ZeroCell[] {
  return style === 'single-zero'
    ? [{ id: '0', rowStart: 0, rowEnd: 3 }]
    : [
        { id: '00', rowStart: 0, rowEnd: 1.5 },
        { id: '0', rowStart: 1.5, rowEnd: 3 },
      ];
}

export function zeroIds(style: WheelStyle): readonly PocketId[] {
  return style === 'single-zero' ? ['0'] : ['0', '00'];
}

/** All pockets on the felt for a style, zeros first. */
export function allFeltPockets(style: WheelStyle): readonly PocketId[] {
  return [...zeroIds(style), ...NUMBER_IDS];
}

// ---------------------------------------------------------------------------
// Legal bet enumeration
// ---------------------------------------------------------------------------

const id = (value: number): PocketId => String(value) as PocketId;

/** Splits touching a zero, which differ between the two felts. */
function zeroSplits(style: WheelStyle): PocketId[][] {
  return style === 'single-zero'
    ? [['0', '1'], ['0', '2'], ['0', '3']]
    : [['0', '00'], ['0', '1'], ['0', '2'], ['00', '2'], ['00', '3']];
}

/** Two-pocket bets: adjacent horizontally or vertically on the grid. */
export function legalSplits(style: WheelStyle): PocketId[][] {
  const out: PocketId[][] = [];
  for (let col = 0; col < FELT_COLS; col += 1) {
    for (let row = 0; row < FELT_ROWS; row += 1) {
      if (col < FELT_COLS - 1) {
        out.push([id(feltNumber(col, row)), id(feltNumber(col + 1, row))]);
      }
      if (row < FELT_ROWS - 1) {
        out.push([id(feltNumber(col, row)), id(feltNumber(col, row + 1))]);
      }
    }
  }
  out.push(...zeroSplits(style));
  return out;
}

/**
 * Three-pocket bets. The twelve numbered streets, plus the zero trios, which
 * share the street payout of 11:1.
 */
export function legalStreets(style: WheelStyle): PocketId[][] {
  const out: PocketId[][] = [];
  for (let col = 0; col < FELT_COLS; col += 1) {
    out.push([id(col * 3 + 1), id(col * 3 + 2), id(col * 3 + 3)]);
  }
  if (style === 'single-zero') {
    out.push(['0', '1', '2'], ['0', '2', '3']);
  } else {
    out.push(['0', '1', '2'], ['00', '2', '3']);
  }
  return out;
}

/**
 * Four-pocket bets: 2×2 blocks on the grid. The single-zero felt adds the
 * "first four" (0-1-2-3), which also pays 8:1.
 */
export function legalCorners(style: WheelStyle): PocketId[][] {
  const out: PocketId[][] = [];
  for (let col = 0; col < FELT_COLS - 1; col += 1) {
    for (let row = 0; row < FELT_ROWS - 1; row += 1) {
      out.push([
        id(feltNumber(col, row)),
        id(feltNumber(col + 1, row)),
        id(feltNumber(col, row + 1)),
        id(feltNumber(col + 1, row + 1)),
      ]);
    }
  }
  if (style === 'single-zero') out.push(['0', '1', '2', '3']);
  return out;
}

/** Six-pocket bets: two adjacent streets. */
export function legalSixLines(): PocketId[][] {
  const out: PocketId[][] = [];
  for (let col = 0; col < FELT_COLS - 1; col += 1) {
    out.push([
      id(col * 3 + 1), id(col * 3 + 2), id(col * 3 + 3),
      id(col * 3 + 4), id(col * 3 + 5), id(col * 3 + 6),
    ]);
  }
  return out;
}

/** The double-zero five-number bet. The worst bet on the table at −7.89%. */
export function basketPockets(style: WheelStyle): PocketId[] | null {
  return style === 'double-zero' ? ['0', '00', '1', '2', '3'] : null;
}

/** Pockets in a column, 1–3. Column 1 is 1, 4, 7 … 34. */
export function columnPockets(column: number): PocketId[] {
  const out: PocketId[] = [];
  for (let value = column; value <= 36; value += 3) out.push(id(value));
  return out;
}

/** Pockets in a dozen, 1–3. */
export function dozenPockets(dozen: number): PocketId[] {
  const out: PocketId[] = [];
  const start = (dozen - 1) * 12 + 1;
  for (let value = start; value < start + 12; value += 1) out.push(id(value));
  return out;
}
